/**
 * BackgroundTaskLayer — Sprint 4C
 * App-wide. Stays SILENT while a background task runs (progress lives in the
 * native notification — no persistent in-app bar). It surfaces a small,
 * dismissible chip only on completion/error, carrying the gesture action the
 * browser requires for share/download (the result Blob/object rides on the event
 * detail, same page — no serialization).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import autoBackupManager from '../utils/autoBackupManager';
import { clearNotification } from '../utils/notify';
import theme from '../styles/theme';

export default function BackgroundTaskLayer() {
  const { t } = useTranslation('common');
  const [card, setCard] = useState(null); // { id, kind, status, result } | null

  useEffect(() => {
    const onDone = (e) => setCard({ ...e.detail, status: 'done' });
    const onError = (e) => setCard({ ...e.detail, status: 'error' });
    window.addEventListener('wayfare-task-done', onDone);
    window.addEventListener('wayfare-task-error', onError);
    return () => {
      window.removeEventListener('wayfare-task-done', onDone);
      window.removeEventListener('wayfare-task-error', onError);
    };
  }, []);

  if (!card) return null;

  const dismiss = () => {
    if (card.id) clearNotification(`wf-task-${card.id}`);
    setCard(null);
  };

  const result = card.result || {};

  const shareBackup = () => {
    try {
      const file = autoBackupManager.backupToShareFile(result.backup, result.suffix || '');
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        navigator.share({ files: [file], title: 'Wayfare Backup' })
          .then(() => { autoBackupManager.markBackedUp(); dismiss(); })
          .catch(() => { /* cancelled or unsupported — leave chip for retry */ });
      } else {
        downloadBackup();
      }
    } catch { downloadBackup(); }
  };

  const downloadBackup = () => {
    autoBackupManager.downloadBackup(result.backup)
      .then(() => { autoBackupManager.markBackedUp(); dismiss(); })
      .catch(() => {});
  };

  const downloadBlob = () => {
    try {
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename || 'wayfare.pdf';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      dismiss();
    } catch { /* ignore */ }
  };

  const isError = card.status === 'error';
  const isBackup = card.kind === 'backup';
  const uploaded = !!result.uploaded; // backup went straight to Drive — nothing to do

  return (
    <div style={S.wrap} role="status" aria-live="polite">
      <div style={{ ...S.card, ...(isError ? S.cardError : null) }}>
        <span style={S.label}>
          {isError ? t('tasks.failed')
            : uploaded ? t('tasks.backupDrive')
            : isBackup ? t('tasks.backupReady')
            : t('tasks.pdfReady')}
        </span>

        {!isError && isBackup && !uploaded && (
          <>
            <button type="button" style={S.action} onClick={shareBackup}>{t('tasks.share')}</button>
            <button type="button" style={S.actionGhost} onClick={downloadBackup}>{t('tasks.download')}</button>
          </>
        )}
        {!isError && !isBackup && result.blob && (
          <button type="button" style={S.action} onClick={downloadBlob}>{t('tasks.download')}</button>
        )}

        <button type="button" style={S.close} onClick={dismiss} aria-label={t('tasks.dismiss')}>✕</button>
      </div>
    </div>
  );
}

const S = {
  wrap: {
    position: 'fixed', left: 0, right: 0,
    bottom: 'calc(env(safe-area-inset-bottom, 0px) + 84px)',
    display: 'flex', justifyContent: 'center', padding: '0 16px',
    zIndex: 1300, pointerEvents: 'none',
  },
  card: {
    pointerEvents: 'auto', maxWidth: 440, width: '100%',
    background: theme.card, border: `1px solid ${theme.creamDeep}`,
    borderLeft: `3px solid ${theme.terracotta}`, borderRadius: 12,
    boxShadow: '0 6px 24px rgba(26,51,64,0.18)',
    padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8,
    animation: 'wf-whisper-in 320ms ease-out',
  },
  cardError: { borderLeftColor: '#A8322B' },
  label: { flex: 1, fontSize: 13.5, fontWeight: 600, color: theme.brownDark },
  action: {
    background: theme.terracotta, color: '#fff', border: 'none',
    borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
  actionGhost: {
    background: 'transparent', color: theme.blueDeep, border: `1px solid ${theme.bluePale}`,
    borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
  close: {
    background: 'transparent', border: 'none', color: theme.sand,
    fontSize: 14, fontWeight: 700, cursor: 'pointer', padding: '4px 6px',
  },
};
